// Bibliography URL styling — small pure helpers used by SectionRenderer and
// the BlogDisplay admin live preview. The goal is subtle citation links that
// don't dominate the reference list; every style below sets
// `target="_blank" rel="noopener noreferrer"` so links open in a new tab.

// Match http/https/www URLs. Kept lenient on trailing punctuation so common
// citation endings (`)`, `.`, `,`) are stripped from the link and remain in
// the surrounding text.
const URL_RE = /\bhttps?:\/\/[^\s<>"']+|www\.[^\s<>"']+/gi;
const TRAILING_PUNCT_RE = /[),.;:!?'"”\]]+$/;

/**
 * Split a reference string into an alternating array of text spans and URL
 * matches. Trailing punctuation is peeled off each URL and pushed back into
 * the next text span so citation endings render naturally.
 */
export function splitBibliographyUrls(text) {
  if (!text || typeof text !== "string") return [{ type: "text", value: String(text ?? "") }];
  const out = [];
  let idx = 0;
  URL_RE.lastIndex = 0;
  let m;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > idx) out.push({ type: "text", value: text.slice(idx, m.index) });
    let raw = m[0];
    let trailing = "";
    const t = raw.match(TRAILING_PUNCT_RE);
    if (t) {
      trailing = t[0];
      raw = raw.slice(0, -trailing.length);
    }
    const href = raw.startsWith("www.") ? `https://${raw}` : raw;
    out.push({ type: "url", value: raw, href });
    if (trailing) out.push({ type: "text", value: trailing });
    idx = m.index + m[0].length;
  }
  if (idx < text.length) out.push({ type: "text", value: text.slice(idx) });
  if (out.length === 0) out.push({ type: "text", value: text });
  return out;
}

// Every style's anchor className is intentionally quiet — no bold, no bright
// color. `subtle` reads as body text with a hover underline; `underline` adds
// a permanent underline; `muted` shrinks the URL; `bracketed`/`icon` collapse
// the URL entirely so the eye stops on the citation, not the link.
export const BIBLIOGRAPHY_URL_STYLES = [
  {
    id: "subtle",
    label: "Subtle",
    hint: "Same colour as body text, underline only on hover.",
    anchorClass: "text-slate-600 dark:text-slate-400 no-underline hover:underline decoration-slate-400 underline-offset-2 break-all",
    label_short: "Aa",
  },
  {
    id: "muted",
    label: "Muted",
    hint: "Smaller, tertiary colour, never underlined.",
    anchorClass: "text-[0.85em] text-slate-500 dark:text-slate-500 no-underline hover:text-slate-700 dark:hover:text-slate-300 break-all",
    label_short: "aa",
  },
  {
    id: "underline",
    label: "Underline",
    hint: "Same colour as body text with a permanent underline.",
    anchorClass: "text-slate-700 dark:text-slate-300 underline decoration-slate-300 dark:decoration-slate-600 underline-offset-2 hover:decoration-slate-500 break-all",
    label_short: "A͟a",
  },
  {
    id: "bracketed",
    label: "Bracketed",
    hint: "URL hidden behind “[link]”, minimal footprint.",
    anchorClass: "text-slate-500 dark:text-slate-500 no-underline hover:text-slate-700 dark:hover:text-slate-300 hover:underline decoration-slate-400 underline-offset-2",
    displayAs: "bracket",
  },
  {
    id: "icon",
    label: "Icon only",
    hint: "URL hidden, replaced with a tiny ↗ marker.",
    anchorClass: "inline-block text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 no-underline align-baseline",
    displayAs: "icon",
  },
];

export const BIBLIOGRAPHY_URL_STYLE_MAP = Object.fromEntries(
  BIBLIOGRAPHY_URL_STYLES.map(s => [s.id, s]),
);

/**
 * Return the style descriptor for the given id, falling back to `subtle`.
 */
export function getBibliographyUrlStyle(styleId) {
  return BIBLIOGRAPHY_URL_STYLE_MAP[styleId] || BIBLIOGRAPHY_URL_STYLE_MAP.subtle;
}
